// File: src/components/web/TermsAndConditions.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

const container = "mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8";
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" },
};

const LAST_UPDATED = "10/07/2026";

const scrollTo = (id) => (e) => {
  e?.preventDefault?.();
  const targetId = (id || "").replace(/^#/, "");
  const el = document.getElementById(targetId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const base = window.location.hash.split("#")[1] || "";
    const basePath = base.includes("/") ? base.split("?")[0] : base;
    window.history.replaceState(null, "", `#${basePath}`);
  } else {
    window.location.hash = `#${targetId}`;
  }
};

const toc = [
  ["Alcance del servicio", "alcance"],
  ["Modalidades, calidad y precios", "modalidades"],
  ["Programación, corte y tiempos", "programacion"],
  ["Paquetes, embalaje y restricciones", "embalaje"],
  ["Facturación, cargos y add-ons", "precios"],
  ["Contraentrega y medios de pago", "contraentrega"],
  ["Inventario, almacén y fulfillment", "inventario"],
  ["Entrega, tracking y cliente final", "entrega"],
  ["Reintentos, devoluciones y retornos", "devoluciones"],
  ["Almacenamiento e inactividad", "almacenamiento"],
  ["Responsabilidad y limitaciones", "responsabilidad"],
  ["Fuerza mayor", "fuerza-mayor"],
  ["Obligaciones del cliente", "obligaciones"],
  ["Datos personales", "datos"],
  ["Suspensión y terminación", "suspension"],
  ["Propiedad intelectual", "propiedad"],
  ["Cambios a los Términos", "cambios"],
  ["Ley aplicable y jurisdicción", "ley"],
  ["Contacto", "contacto"],
];

const serviceTiers = [
  {
    name: "Clásico",
    price: "S/ 9.90",
    tone: "border-slate-200 bg-white",
    headline: "Entrega estándar para tiendas que gestionan su propia calidad de pedido.",
    bullets: [
      "Registro de punto de recojo y puntos de entrega.",
      "Operación de entrega el mismo día, sujeta a cobertura, corte y capacidad.",
      "Mensajería automática y atención operativa al cliente final.",
      "Un intento de entrega incluido.",
    ],
  },
  {
    name: "Coordinado",
    price: "S/ 14.90",
    tone: "border-brand-primary/30 bg-brand-primary/5",
    headline: "Modalidad recomendada para e-commerce que busca elevar su tasa de entrega efectiva.",
    bullets: [
      "Mayor coordinación operativa con destinatario y tienda.",
      "Un reintento adicional incluido en distritos elegibles.",
      "Contraentrega completa con los medios de pago habilitados por FAST MAX.",
      "Inventario, almacén activo, fulfillment y packing cuando el producto está en FAST MAX.",
      "Tracking reforzado, ubicación del conductor cuando esté disponible e interfaz para cliente final.",
    ],
  },
  {
    name: "Premium",
    price: "S/ 19.90",
    tone: "border-amber-300 bg-amber-50",
    headline: "Prioridad operativa para pedidos urgentes o de mayor sensibilidad comercial.",
    bullets: [
      "Incluye los beneficios de Coordinado, sujeto a elegibilidad operativa.",
      "Prioridad de ruta: se procura despachar entre las primeras entregas posibles de la ruta.",
      "Entrega lo más rápido posible dentro de la operación del día.",
      "Hasta 10 minutos adicionales de espera razonable en destino.",
      "Mayor seguimiento operativo para incidencias críticas.",
    ],
  },
];

const afternoonOnlyDistricts = [
  "Ancón",
  "Carabayllo",
  "Chaclacayo",
  "Cieneguilla",
  "Lurigancho / Chosica",
  "Lurín",
  "Mi Perú",
  "Pachacámac",
  "Puente Piedra",
  "Pucusana",
  "Punta Hermosa",
  "Punta Negra",
  "San Bartolo",
  "Santa María del Mar",
  "Santa Rosa",
  "Ventanilla",
];

export default function TermsAndConditions() {
  return (
    <div className="bg-white text-slate-900">
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className={`${container} flex h-16 items-center justify-between`}>
          <Link to="/" className="flex items-center gap-2" aria-label="FAST MAX">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="FAST MAX" className="h-8 w-auto" />
            <span className="hidden sm:inline text-lg font-extrabold tracking-wide" style={{ color: brand.primary }}>
              FAST MAX
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 sm:text-sm sm:px-4 sm:py-2"
          >
            Volver al inicio
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </header>

      <main>
        <section className="bg-white">
          <div className={`${container} py-10 md:py-14`}>
            <motion.h1 {...fadeUp} className="text-3xl md:text-4xl font-extrabold" style={{ color: brand.primary }}>
              Términos y Condiciones de Servicio
            </motion.h1>
            <motion.p {...fadeUp} transition={{ delay: 0.05 }} className="mt-2 text-slate-600">
              Última actualización: {LAST_UPDATED}
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6"
            >
              <p>
                Estos Términos y Condiciones regulan el uso de los servicios de logística, transporte, contraentrega,
                inventario, fulfillment y tecnología operativa que brinda <strong>FAST MAX</strong> en Lima Metropolitana
                y Callao, dentro de la cobertura publicada. Al crear una cuenta, cargar pedidos, registrar productos o
                solicitar un envío, el cliente o tienda acepta estos Términos.
              </p>
              <p className="mt-3 text-slate-600">
                FAST MAX usa tecnología, mensajería, trazabilidad, datos operativos e inteligencia logística para mejorar
                la probabilidad de entrega efectiva; sin embargo, ninguna modalidad garantiza la aceptación del paquete,
                el pago del destinatario ni un resultado comercial específico.
              </p>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="mt-8 grid gap-2 sm:grid-cols-2">
              {toc.map(([label, id]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold hover:bg-slate-50"
                  onClick={scrollTo(id)}
                >
                  {label}
                </a>
              ))}
            </motion.div>
            <Section id="alcance" title="1. Alcance del servicio">
              <ul className="list-outside space-y-2 pl-4 text-slate-700">
                <li>
                  <b>Ámbito:</b> FAST MAX presta servicios de recogida, recepción, almacenamiento operativo, preparación,
                  transporte y entrega de paquetes dentro de Lima Metropolitana y Callao, según cobertura y capacidad
                  operativa vigente.
                </li>
                <li>
                  <b>Especialidad:</b> FAST MAX está especializado en paquetes pequeños. Como referencia, el paquete no
                  debe exceder un volumen similar al de una caja de zapatos ni superar <b>1 kg</b>, salvo aprobación previa
                  o política especial publicada por FAST MAX.
                </li>
                <li>
                  <b>Naturaleza del servicio:</b> FAST MAX actúa como operador logístico y tecnológico. No es vendedor,
                  fabricante ni intermediario del producto enviado, y no responde por la decisión del destinatario de
                  aceptar, rechazar o pagar un pedido.
                </li>
                <li>
                  <b>Plataforma:</b> el cliente puede gestionar pedidos, inventario, contraentrega, tracking, atención e
                  información operativa mediante la plataforma FAST MAX, carga masiva por Excel, interfaces para cliente
                  final, mensajería automática y futuras integraciones API.
                </li>
              </ul>
            </Section>

            <Section id="modalidades" title="2. Modalidades, calidad y precios">
              <p className="text-slate-700">
                Las modalidades separan niveles de coordinación, visibilidad y prioridad operativa. La diferencia de
                precio busca reflejar mayor esfuerzo logístico, mayor trazabilidad y herramientas diseñadas para mejorar
                la probabilidad de entrega efectiva.
              </p>
              <div className="mt-5 grid gap-4 md:grid-cols-3">
                {serviceTiers.map((tier) => (
                  <TierCard key={tier.name} tier={tier} />
                ))}
              </div>
              <div className="mt-5 rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-4 text-sm text-slate-700">
                <p>
                  <b>Importante:</b> Coordinado y Premium están diseñados para aumentar la calidad operativa y reducir
                  fricciones de entrega mediante coordinación, COD, inventario, fulfillment, tracking y atención; no
                  constituyen una garantía absoluta de entrega, cobro, aceptación del paquete o incremento de ingresos.
                </p>
              </div>
            </Section>

            <Section id="programacion" title="3. Programación, corte y tiempos">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  <b>Corte de recojo:</b> el cliente puede registrar un recojo en tienda hasta las <b>12:00 PM</b>,
                  salvo comunicación operativa distinta o disponibilidad especial publicada por FAST MAX.
                </li>
                <li>
                  <b>Corte de entrega same-day:</b> las entregas para el mismo día pueden registrarse hasta las{" "}
                  <b>2:00 PM</b>, sujetas a cobertura, capacidad y validación operativa.
                </li>
                <li>
                  <b>Puntos de entrega de un recojo activo:</b> si ya existe un recojo programado, el cliente puede
                  registrar o completar puntos de entrega hasta que el conductor llegue al punto de recojo, siempre que
                  el sistema lo permita y no afecte la ruta ya planificada.
                </li>
                <li>
                  <b>Entrega el mismo día:</b> los pedidos aceptados dentro de los cortes se programan para entrega el mismo
                  día, sujetos a cobertura, capacidad, tráfico, clima, validación de dirección, disponibilidad del
                  destinatario y otras condiciones operativas.
                </li>
                <li>
                  <b>Distritos de operación solo tarde:</b> algunos distritos tienen ventanas disponibles principalmente
                  de <b>4:00 PM a 9:00 PM</b>. El sistema validará automáticamente la disponibilidad por distrito al
                  momento de registrar o coordinar el pedido.
                </li>
                <li>
                  <b>Premium:</b> no amplía por sí solo el horario de corte. Su beneficio principal es prioridad operativa:
                  FAST MAX procurará entregar lo más rápido posible y ubicar el pedido entre las primeras entregas
                  factibles de la ruta, sin garantizar una hora exacta.
                </li>
              </ul>
            </Section>

            <Section id="embalaje" title="4. Paquetes, embalaje y restricciones">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  El cliente es responsable del embalaje adecuado, etiquetado correcto, dirección completa, referencias,
                  contacto del destinatario y descripción operativa suficiente del pedido.
                </li>
                <li>
                  Si el producto se encuentra almacenado en FAST MAX y el servicio contratado incluye fulfillment,
                  FAST MAX podrá preparar y empacar el producto según estándares operativos razonables y materiales
                  disponibles.
                </li>
                <li>
                  No se aceptan mercancías prohibidas por ley o política interna, incluyendo de forma enunciativa:
                  armas, explosivos, sustancias peligrosas, drogas, artículos ilícitos, dinero o valores fuera del
                  flujo autorizado de contraentrega, animales vivos, productos perecibles sin condiciones adecuadas,
                  documentos sensibles sin acuerdo previo y objetos de alto valor sin autorización.
                </li>
                <li>
                  FAST MAX puede rechazar, pausar o interrumpir un servicio si detecta incumplimientos de tamaño, peso,
                  embalaje, documentación, seguridad o restricciones de contenido.
                </li>
              </ul>
            </Section>

            <Section id="precios" title="5. Facturación, cargos y add-ons">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Las tarifas base vigentes son: <b>Clásico S/ 9.90</b>, <b>Coordinado S/ 14.90</b> y <b>Premium S/ 19.90</b>,
                  salvo promociones, descuentos legacy, acuerdos comerciales o tarifas especiales expresamente aceptadas
                  por FAST MAX.
                </li>
                <li>
                  La tarifa aplicable se bloquea como <b>snapshot de precio</b> al momento de registrar el envío o cerrar
                  la orden en el sistema. Las vistas previas de precio son referenciales hasta su confirmación por el
                  backend o canal oficial de FAST MAX.
                </li>
                <li>
                  Reintentos adicionales, devoluciones, retornos, almacenaje extraordinario, zonas especiales, cambios
                  de dirección, empaques especiales u otros add-ons presentes o futuros pueden generar cargos
                  adicionales, siempre que sean comunicados en la plataforma o por canales oficiales.
                </li>
                <li>
                  La facturación, liquidación y medios de pago a FAST MAX se informan en el panel, comprobantes,
                  calendario operativo o canales oficiales.
                </li>
              </ul>
            </Section>

            <Section id="contraentrega" title="6. Contraentrega y medios de pago">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Cuando el cliente habilita la <b>contraentrega</b> o COD, FAST MAX actúa como recaudador por cuenta del
                  cliente conforme al monto indicado en la orden y a los medios de pago habilitados por FAST MAX.
                </li>
                <li>
                  Coordinado y Premium incluyen una experiencia COD reforzada: coordinación de cobro, aceptación de los
                  medios de pago disponibles, manejo de efectivo cuando aplique, registro operativo y conciliación en la
                  plataforma.
                </li>
                <li>
                  Los medios de pago pueden incluir efectivo y medios digitales habilitados por FAST MAX o sus proveedores.
                  FAST MAX puede modificar, suspender o limitar medios de pago por seguridad, fraude, disponibilidad
                  técnica o políticas de proveedores.
                </li>
                <li>
                  FAST MAX no es responsable si el destinatario rechaza el paquete, no cuenta con fondos, desconoce el
                  pedido, se niega a pagar o entrega información incorrecta. En esos casos, el pedido podrá ser
                  reintentado, retornado o gestionado según modalidad y política aplicable.
                </li>
                <li>
                  La liquidación y depósito de montos recaudados se realizan conforme al calendario operativo,
                  conciliación disponible y validaciones de seguridad de FAST MAX.
                </li>
              </ul>
            </Section>
            <Section id="inventario" title="7. Inventario, almacén y fulfillment">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  En las modalidades que lo habiliten, FAST MAX puede mantener inventario del cliente en almacén para
                  facilitar entregas, preparación de pedidos, packing, fulfillment y salida rápida desde operación propia.
                </li>
                <li>
                  El inventario visible en la pestaña de inventario del usuario es una herramienta operativa. El cliente
                  debe revisar existencias, movimientos, incidencias y conciliaciones de forma periódica.
                </li>
                <li>
                  El almacenamiento es gratuito mientras el producto se mantenga <b>activo</b>. Para estos Términos, un
                  producto puede considerarse inactivo si no registra movimiento, salida, venta, reposición o instrucción
                  operativa durante <b>4 semanas</b>.
                </li>
                <li>
                  Tras 4 semanas de inactividad, FAST MAX podrá solicitar instrucciones y coordinar el envío del producto
                  a la ubicación indicada por el cliente. Si el cliente no responde, no indica destino o no recibe el
                  producto dentro del plazo operativo comunicado, el inventario podrá considerarse abandonado y quedar
                  sujeto a disposición, merma o baja operativa sin responsabilidad por custodia indefinida.
                </li>
              </ul>
            </Section>

            <Section id="entrega" title="8. Entrega, tracking y cliente final">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  La entrega puede requerir prueba de entrega, como foto, firma, nombre de receptor, validación por PIN,
                  estado en plataforma u otro método razonable.
                </li>
                <li>
                  FAST MAX puede ofrecer al destinatario una interfaz de cliente final con resumen del pedido,
                  explicación de entrega, ventana horaria, estado operativo, comunicaciones y opciones disponibles.
                </li>
                <li>
                  En Coordinado y Premium, FAST MAX podrá mostrar ubicación del conductor, ETA o hitos de ruta cuando la
                  tecnología, permisos, conectividad y seguridad operativa lo permitan. La ubicación es referencial y no
                  debe interpretarse como garantía de hora exacta.
                </li>
                <li>
                  Si la dirección es incorrecta, el destinatario no está disponible, no responde, solicita cambio o
                  reprogramación, o existen restricciones de acceso, el envío puede no entregarse en la primera visita.
                </li>
              </ul>
            </Section>

            <Section id="devoluciones" title="9. Reintentos, devoluciones y retornos">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  <b>Clásico:</b> incluye un intento de entrega. Reintentos, cambios o retornos pueden generar cargos
                  adicionales.
                </li>
                <li>
                  <b>Coordinado:</b> incluye un reintento adicional sin costo en distritos elegibles, siempre que la falla
                  no derive de contenido prohibido, datos falsos, solicitud indebida, falta de pago del cliente a FAST MAX
                  u otra causa excluida.
                </li>
                <li>
                  <b>Premium:</b> incluye el reintento elegible de Coordinado y agrega prioridad operativa; no convierte
                  distritos no elegibles en distritos con reintento incluido.
                </li>
                <li>
                  Los distritos de operación solo tarde o fuera de zonas centrales pueden no incluir reintento gratuito.
                  La elegibilidad se valida automáticamente en la plataforma al momento del registro. Según el catálogo
                  operativo vigente, los distritos que pueden quedar sin reintento incluido son: {afternoonOnlyDistricts.join(", ")}.
                </li>
                <li>
                  Los retornos de paquetes al cliente se realizan según programación de rutas, disponibilidad y política
                  operativa. Rechazos, falta de pago en COD o ausencia del destinatario no son atribuibles a FAST MAX.
                </li>
              </ul>
            </Section>

            <Section id="almacenamiento" title="10. Almacenamiento e inactividad">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Envíos no entregados, retornados o pendientes de instrucción pueden permanecer en almacenamiento
                  temporal por un plazo operativo razonable.
                </li>
                <li>
                  El cliente debe atender avisos, incidencias, devoluciones e instrucciones de retiro. Si no responde o
                  no coordina destino, FAST MAX podrá tratar el paquete como abandonado, aplicando cargos, baja operativa
                  o disposición conforme a política interna y normativa aplicable.
                </li>
              </ul>
            </Section>

            <Section id="responsabilidad" title="11. Responsabilidad y limitaciones">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  FAST MAX responde por daños imputables directamente a su operación de transporte o custodia, conforme a
                  la legislación aplicable, estos Términos y sus políticas internas.
                </li>
                <li>
                  Salvo contratación expresa de seguro o cobertura adicional, la responsabilidad por pérdida o daño se
                  limita a los límites establecidos en la política de responsabilidad de FAST MAX, disponible en el panel
                  o por solicitud.
                </li>
                <li>
                  FAST MAX no responde por contenido prohibido, defectuoso, frágil o insuficientemente embalado;
                  decisiones del destinatario; falta de pago; información incorrecta; demoras por fuerza mayor; fallas de
                  proveedores externos; pérdidas comerciales indirectas; lucro cesante; pérdida de oportunidad; daño
                  reputacional o ingresos esperados por la tienda.
                </li>
                <li>
                  Las modalidades Coordinado y Premium mejoran coordinación, prioridad o visibilidad, pero no garantizan
                  aceptación del paquete, cobro, venta final, satisfacción del destinatario ni tasa específica de éxito.
                </li>
              </ul>
            </Section>

            <Section id="fuerza-mayor" title="12. Fuerza mayor">
              <p className="text-slate-700">
                Se entiende por fuerza mayor cualquier evento fuera del control razonable de FAST MAX, incluyendo
                desastres naturales, bloqueos, huelgas, congestión extraordinaria, fallas generalizadas de servicios,
                restricciones de autoridad, inseguridad, accidentes, emergencias sanitarias o fallas de proveedores. En
                estos casos, las obligaciones podrán suspenderse, reprogramarse o ajustarse sin responsabilidad por
                retrasos no imputables.
              </p>
            </Section>

            <Section id="obligaciones" title="13. Obligaciones del cliente">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>Proveer datos exactos de destinatario, dirección, distrito, referencias, contacto, monto COD y producto.</li>
                <li>Cumplir con límites de tamaño, peso, embalaje, contenido permitido y políticas de inventario.</li>
                <li>Informar a sus destinatarios sobre la entrega, contraentrega, medios de pago y comunicaciones operativas.</li>
                <li>Atender conciliaciones, incidencias, solicitudes de información, retornos e inventario inactivo.</li>
                <li>Usar la plataforma de buena fe y no registrar pedidos fraudulentos, simulados o contrarios a la ley.</li>
              </ul>
            </Section>

            <Section id="datos" title="14. Datos personales">
              <p className="text-slate-700">
                FAST MAX trata datos personales de clientes, tiendas, destinatarios, conductores y participantes
                operativos para finalidades de logística, entrega, contraentrega, inventario, fulfillment, atención,
                seguridad, trazabilidad, analítica e inteligencia operativa, conforme a la normativa peruana de
                protección de datos personales (Ley N.° 29733 y su reglamento). El cliente declara haber informado a sus
                destinatarios y contar con las autorizaciones necesarias para comunicar sus datos a FAST MAX.
              </p>
            </Section>
            <Section id="suspension" title="15. Suspensión y terminación">
              <p className="text-slate-700">
                FAST MAX podrá suspender o finalizar el servicio ante incumplimientos, falta de pago, fraude, abuso de
                plataforma, productos prohibidos, riesgo operativo, riesgo de seguridad, datos falsos o razones
                justificadas de continuidad operativa, informando al cliente por los canales disponibles cuando sea
                razonablemente posible.
              </p>
            </Section>

            <Section id="propiedad" title="16. Propiedad intelectual">
              <p className="text-slate-700">
                La plataforma, marcas, software, interfaces, algoritmos, paneles, documentación, diseños, APIs,
                automatizaciones y demás elementos son propiedad de FAST MAX o sus licenciantes. No se concede licencia
                distinta al uso necesario para operar los servicios contratados.
              </p>
            </Section>

            <Section id="cambios" title="17. Cambios a los Términos">
              <p className="text-slate-700">
                FAST MAX podrá actualizar estos Términos para reflejar cambios legales, operativos, tecnológicos,
                comerciales, de precios, modalidades o add-ons. La versión vigente estará disponible en el sitio o
                plataforma y podrá comunicarse por medios electrónicos.
              </p>
            </Section>

            <Section id="ley" title="18. Ley aplicable y jurisdicción">
              <p className="text-slate-700">
                Estos Términos se rigen por las leyes de la República del Perú. Para cualquier controversia, las partes
                se someten a la jurisdicción de los juzgados y tribunales de Lima, salvo disposición legal imperativa
                distinta.
              </p>
            </Section>

            <Section id="contacto" title="19. Contacto">
              <p className="text-slate-700">
                ¿Dudas o consultas? Escríbenos a <a href="mailto:contacto@fastmax.pe" className="underline">contacto@fastmax.pe</a> o contáctanos por
                WhatsApp a través de nuestros canales oficiales.
              </p>
            </Section>

            <div className="mt-10 flex justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm"
                style={{ background: brand.primary }}
              >
                Volver al inicio
                <ChevronRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function TierCard({ tier }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${tier.tone}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-extrabold" style={{ color: brand.primary }}>{tier.name}</h3>
        <span className="rounded-full bg-white px-3 py-1 text-sm font-black text-slate-900 shadow-sm">{tier.price}</span>
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-800">{tier.headline}</p>
      <ul className="mt-3 space-y-2 text-sm text-slate-700">
        {tier.bullets.map((bullet) => (
          <li key={bullet} className="flex gap-2">
            <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: brand.accentGreen }} />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <motion.h2 {...fadeUp} className="mt-10 text-xl font-extrabold" style={{ color: brand.primary }}>
        {title}
      </motion.h2>
      <motion.div {...fadeUp} transition={{ delay: 0.02 }} className="mt-3">
        {children}
      </motion.div>
    </section>
  );
}
